import { useState, useRef, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress, Button, Tooltip } from '@mui/material';
import { Send, UploadFile, FileDownload } from '@mui/icons-material';
import {
  DndContext, DragOverlay, PointerSensor,
  useSensor, useSensors, closestCenter,
} from '@dnd-kit/core';
import api from '../../config/api';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { CandidateCard } from './AtsCandidateCard';
import AtsStageColumn from './AtsStageColumn';
import AtsApplicationDrawer from './AtsApplicationDrawer';
import AtsMoveStageDialog from './AtsMoveStageDialog';
import AtsBulkDiscardDialog from './AtsBulkDiscardDialog';
import AtsSendToClientDialog from './AtsSendToClientDialog';
import AtsBulkRequestDocumentsDialog from './AtsBulkRequestDocumentsDialog';
import AtsBlockDialog from './AtsBlockDialog';
import AtsOfferDialog from './AtsOfferDialog';
import AtsHireDialog from './AtsHireDialog';
import AtsValidateDialog from './AtsValidateDialog';
import useOrganizationCountry from '../../hooks/useOrganizationCountry';
import { skippedAfterMoveMessage } from './backgroundCheckPricing';

// Nombre del archivo que manda el backend (Content-Disposition), con tildes
// (filename*) o sin ellas (filename)
const fileNameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const encoded = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch (e) {
      // sigue con filename=
    }
  }
  const plain = /filename\s*=\s*"([^"]+)"/i.exec(disposition) || /filename\s*=\s*([^;]+)/i.exec(disposition);
  return plain ? plain[1].trim() : null;
};

const AtsKanbanPipeline = ({
  position, applications, onApplicationsChange, positionId, isClosed, canEdit = true,
  rejectionTemplate = { subject: '', body: '' }, autoSendDefault = false,
  positionName = '', orgName = '',
  cvScores = {}, onCvScoresRefresh,
  onPositionChange,
}) => {
  const { t, i18n } = useTranslation();
  const { authToken, showAlert, user, organization } = useStore.getState();
  // Revisión de antecedentes: solo organizaciones que operan en Chile
  // (fuera de Chile en espera, PLAN_MASIVO_PORTAL)
  const { operatesInChile, loading: orgCountryLoading } = useOrganizationCountry();
  const reviewAvailable = !orgCountryLoading && operatesInChile;

  // Permisos del bloqueo: bloquear admin+; desbloquear solo super-admin
  const isAdminPlus = ['admin', 'admin-grafton', 'super-admin'].includes(user?.rol);
  const isSuperAdmin = user?.rol === 'super-admin';
  const orgId = position?.organizationId || organization;

  const [activeApplication, setActiveApplication] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [reviewFilter, setReviewFilter] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  // Recorrido de la ficha (navegación entre candidatos): etapa desde la que se
  // abrió y los ids visibles de esa columna, en su orden, al abrirla. null = sin
  // recorrido (Por validar, Descartados, Bloqueados y "Abrir ficha" de los modales)
  const [drawerNav, setDrawerNav] = useState(null); // { stageId, ids }
  // Al pasar de un candidato a otro la ficha aparece sin animación
  const [drawerInstant, setDrawerInstant] = useState(false);
  // Candidato que muestra la ficha (lo leen los pasos que siguen a una
  // llamada al backend, ver handleBlock)
  const openApplicationIdRef = useRef(null);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [pendingMove, setPendingMove] = useState(null);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const [bulkDiscardOpen, setBulkDiscardOpen] = useState(false);
  const [sendToClientOpen, setSendToClientOpen] = useState(false);
  const [bulkDocumentsOpen, setBulkDocumentsOpen] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);
  const [offerTarget, setOfferTarget] = useState(null);
  const [hireTarget, setHireTarget] = useState(null);
  const [validateTarget, setValidateTarget] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Parche puntual de una postulación (p. ej. revisión de antecedentes desde el
  // modal de la etapa, o la que recalcula el drawer al abrirse). Actualización
  // funcional: el modal verifica en lote.
  const handleApplicationPatch = (applicationId, patch) => {
    onApplicationsChange((prev) => prev.map((a) => ((a.uid || a.id) === applicationId ? { ...a, ...patch } : a)));
  };

  // Acción desde el botón de la tarjeta según la etapa (oferta / contratado)
  const handleStageAction = (application, stageType) => {
    if (stageType === 'offer') setOfferTarget(application);
    else if (stageType === 'hired') setHireTarget(application);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const phases = position?.pipeline?.phases || [];
  const currentPhaseId = position?.pipeline?.currentPhaseId;
  const currentPhase = phases.find((p) => p.id === currentPhaseId) || phases[0];

  const columns = [];
  phases.forEach((phase) => {
    (phase.stages || []).forEach((stage) => {
      columns.push({
        ...stage,
        phaseId: phase.id,
        phaseName: phase.name,
        phaseOrder: phase.order,
        // isLocked: isClosed || phase.order !== (currentPhase?.order || 0),
        isLocked: false
      });
    });
  });

  // Bandeja de revisión de antecedentes (F3): filtro transversal a las etapas
  const needsReview = (a) =>
    reviewAvailable &&
    a.backgroundReview?.status === 'requiere_revision' &&
    a.backgroundReview?.decision?.status !== 'approved';
  // "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): fuera de las etapas,
  // en su propia columna, como "Bloqueado"
  const isOutsideStages = (a) => a.status === 'discarded' || a.status === 'blocked' || a.status === 'pending_validation';
  const reviewCount = applications.filter((a) => !isOutsideStages(a) && needsReview(a)).length;

  const getApplicationsByStage = (stageId) =>
    applications.filter((a) =>
      a.currentStageId === stageId && !isOutsideStages(a) &&
      (!reviewFilter || needsReview(a))
    );

  // Score del filtrado CV asociado a la application (mapa applicantId → score)
  const getCvScore = (application) =>
    application?.applicantId != null ? cvScores[application.applicantId] ?? null : null;

  // Columnas solo-lectura al final del kanban
  const discardedApplications = applications.filter((a) => a.status === 'discarded');
  const blockedApplications = applications.filter((a) => a.status === 'blocked');
  const pendingValidationApplications = applications.filter((a) => a.status === 'pending_validation');

  // "Validar": la postulación sale de "Por validar" con el estado que devuelve el backend
  const handleValidated = (application, patch) => {
    const id = application.uid || application.id;
    onApplicationsChange(
      applications.map((a) => ((a.uid || a.id) === id ? { ...a, ...patch } : a))
    );
    setValidateTarget(null);
  };

  // columnIds: ids visibles de la columna de etapa, en su orden (los manda la
  // tarjeta de AtsStageColumn); con ellos se arma el recorrido de la ficha
  const handleCardClick = (application, columnIds = null) => {
    setSelectedApplication(application);
    setDrawerOpen(true);
    setDrawerInstant(false);
    setDrawerNav(Array.isArray(columnIds) && application?.currentStageId
      ? { stageId: application.currentStageId, ids: columnIds }
      : null);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedApplication(null);
    setDrawerNav(null);
    setDrawerInstant(false);
  };

  // Siguen en el recorrido los que siguen en la etapa (activos y en esa
  // etapa): quien se movió, se descartó o se bloqueó sale, tanto al avanzar
  // como al retroceder
  const isInDrawerNavStage = (id) => {
    if (!drawerNav) return false;
    const application = applications.find((a) => (a.uid || a.id) === id);
    return !!application && application.currentStageId === drawerNav.stageId && !isOutsideStages(application);
  };

  // Anterior (step -1) o siguiente (step 1) de fromId, en el orden de la
  // columna. El abierto conserva su lugar aunque ya no esté en la etapa; en
  // los extremos no hay (el recorrido no da la vuelta)
  const getDrawerNeighborId = (fromId, step) => {
    if (!drawerNav) return null;
    const index = drawerNav.ids.indexOf(fromId);
    if (index < 0) return null;
    for (let i = index + step; i >= 0 && i < drawerNav.ids.length; i += step) {
      if (isInDrawerNavStage(drawerNav.ids[i])) return drawerNav.ids[i];
    }
    return null;
  };

  // La ficha pasa a otro candidato del recorrido, con su versión vigente de
  // la lista: se vuelve a montar (key) igual que al abrirla desde el tablero
  const goToApplication = (id) => {
    const target = applications.find((a) => (a.uid || a.id) === id);
    if (!target) return false;
    setSelectedApplication(target);
    setDrawerInstant(true);
    return true;
  };

  // Tras descartar o bloquear al candidato abierto: pasa al siguiente de la
  // etapa; sin siguiente (o sin recorrido), close() como antes
  const goToNextOrClose = (nextId, close) => {
    if (nextId && goToApplication(nextId)) return;
    close();
  };

  // Descartar desde la ficha. Con recorrido, la ficha pasa al siguiente de la
  // etapa (sin siguiente o sin recorrido se cierra, como antes) y la tarjeta
  // sale de la etapa al instante, para que "Anterior" no la muestre mientras
  // responde el backend; si el backend falla, vuelve a su etapa.
  const handleDiscardFromDrawer = async (application, emailOptions) => {
    const id = application.uid || application.id;
    const previousStatus = applications.find((a) => (a.uid || a.id) === id)?.status ?? application.status;
    goToNextOrClose(getDrawerNeighborId(id, 1), closeDrawer);
    onApplicationsChange((prev) => prev.map((a) => ((a.uid || a.id) === id ? { ...a, status: 'discarded' } : a)));
    try {
      await api.patch(
        `/applications/${id}/discard`,
        {
          sendEmail: !!emailOptions?.sendEmail,
          emailSubject: emailOptions?.emailSubject || '',
          emailBody: emailOptions?.emailBody || '',
          senderName: emailOptions?.senderName || '',
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      showAlert(t('ats.detail.discarded'), 'success');
    } catch (err) {
      console.error(err);
      // Vuelve a su etapa (y al recorrido)
      onApplicationsChange((prev) => prev.map((a) => ((a.uid || a.id) === id ? { ...a, status: previousStatus } : a)));
      showAlert(t('ats.detail.discardError'), 'error');
    }
  };

  // Restaurar un candidato descartado: vuelve a estar activo en la etapa donde
  // estaba al descartarlo (currentStageId no se pierde al descartar).
  const handleRestore = async (application) => {
    if (!application) return;
    try {
      const { data } = await api.patch(
        `/applications/${application.uid || application.id}/status`,
        { status: 'active' },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      // Una postulación que nunca se validó vuelve a "Por validar" (fase 0)
      const restoredStatus = data?.response?.data?.status || 'active';
      onApplicationsChange(
        applications.map((a) =>
          (a.uid || a.id) === (application.uid || application.id)
            ? { ...a, status: restoredStatus }
            : a
        )
      );
      setDrawerOpen(false);
      setSelectedApplication(null);
      showAlert(t('ats.detail.restored'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.restoreError'), 'error');
    }
  };

  const handleBlock = async (application, reason) => {
    setBlockTarget(null);
    if (!application) return;
    const id = application.uid || application.id;
    // Siguiente de la etapa, calculado antes de bloquear (con recorrido)
    const nextId = getDrawerNeighborId(id, 1);
    try {
      await api.post(
        `/organizations/${orgId}/blacklist`,
        {
          email: application.candidateEmail,
          name: application.candidateName || null,
          reason: reason || null,
        },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const email = (application.candidateEmail || '').toLowerCase();
      onApplicationsChange((prev) =>
        prev.map((a) =>
          (a.candidateEmail || '').toLowerCase() === email
            ? { ...a, status: 'blocked', blockReason: reason || null }
            : a
        )
      );
      // Con la confirmación del backend, como antes, y solo si la ficha sigue
      // en el bloqueado (si ya se pasó a otro o se cerró, no se la toca): pasa
      // al siguiente de la etapa o se cierra
      if (openApplicationIdRef.current === id) {
        goToNextOrClose(nextId, () => { setDrawerOpen(false); setDrawerInstant(false); });
      }
      showAlert(t('ats.detail.blocked'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.blockError'), 'error');
    }
  };

  const handleUnblock = async (application) => {
    if (!application) return;
    try {
      const emailParam = encodeURIComponent(application.candidateEmail || '');
      await api.delete(
        `/organizations/${orgId}/blacklist/${emailParam}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const email = (application.candidateEmail || '').toLowerCase();
      onApplicationsChange(
        applications.map((a) =>
          (a.candidateEmail || '').toLowerCase() === email
            ? { ...a, status: 'discarded', blockReason: null }
            : a
        )
      );
      setDrawerOpen(false);
      showAlert(t('ats.detail.unblocked'), 'success');
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.blockError'), 'error');
    }
  };

  // Reflejar cambios de la oferta (resultado o envío) en la lista local
  const handleOfferChange = (application, changes) => {
    const id = application.uid || application.id;
    onApplicationsChange(
      applications.map((a) =>
        (a.uid || a.id) === id ? { ...a, ...changes } : a
      )
    );
  };

  // Reflejar los datos de contratación guardados en la lista local
  const handleHireChange = (application, hireData) => {
    const id = application.uid || application.id;
    onApplicationsChange(
      applications.map((a) =>
        (a.uid || a.id) === id ? { ...a, ...hireData } : a
      )
    );
  };

  const handleBulkDiscard = async ({ sendEmail, emailSubject, emailBody, senderName }) => {
    const ids = selectedApplicationIds.filter((id) =>
      applications.some((a) => (a.uid || a.id) === id && a.status !== 'discarded')
    );
    if (ids.length === 0) { setBulkDiscardOpen(false); return; }
    try {
      const res = await api.post(
        `/applications/discard-bulk`,
        { positionId, applicationIds: ids, sendEmail, emailSubject, emailBody, senderName },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        onApplicationsChange(
          applications.map((a) =>
            ids.includes(a.uid || a.id) ? { ...a, status: 'discarded' } : a
          )
        );
        setSelectedApplicationIds([]);
        setBulkDiscardOpen(false);
        const s = res.data.response.data;
        showAlert(t('ats.detail.bulkDiscardSuccess', { count: s?.discarded ?? ids.length }), 'success');
      } else {
        showAlert(t('ats.detail.discardError'), 'error');
      }
    } catch (err) {
      console.error(err);
      showAlert(t('ats.detail.discardError'), 'error');
    }
  };

  // Enviar al cliente las fichas (CV + resumen) de los seleccionados: un solo correo.
  const handleSendToClient = async ({ clientEmail, subject, body, senderName }) => {
    const ids = selectedApplicationIds.filter((id) =>
      applications.some((a) => (a.uid || a.id) === id)
    );
    if (ids.length === 0) { setSendToClientOpen(false); return; }
    try {
      const res = await api.post(
        `/applications/shortlist-client-email`,
        { clientEmail, subject, body, senderName, applicationIds: ids },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (res.data.response?.status_response === 'OK') {
        const d = res.data.response.data || {};
        const missing = Array.isArray(d.missing) ? d.missing : [];
        setSendToClientOpen(false);
        setSelectedApplicationIds([]);
        showAlert(t('ats.sendClient.success', { email: clientEmail }), 'success');
        if (missing.length > 0) {
          const detail = missing.map((m) => {
            const parts = [];
            if (m.missingCv) parts.push('CV');
            if (m.missingResumen) parts.push(t('ats.sendClient.resumenWord'));
            return `${m.candidateName} (${parts.join(', ')})`;
          }).join('; ');
          showAlert(t('ats.sendClient.partialWarning', { detail }), 'warning');
        }
      } else {
        showAlert(res.data.response?.message || t('ats.sendClient.error'), 'error');
      }
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.response?.message || t('ats.sendClient.error');
      showAlert(msg, 'error');
    }
  };

  const handleSelectionChange = (application, checked) => {
    const appId = application.uid || application.id;

    setSelectedApplicationIds((prev) => {
      if (checked) {
        if (prev.includes(appId)) return prev;
        return [...prev, appId];
      }

      return prev.filter((id) => id !== appId);
    });
  };

  const handleBulkSelectionChange = (targetApplications, checked) => {
    const ids = targetApplications.map((application) => application.uid || application.id);

    setSelectedApplicationIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...ids]));
      }

      return prev.filter((id) => !ids.includes(id));
    });
  };

  const handleClearSelection = () => {
    setSelectedApplicationIds([]);
  };

  // Excel con TODOS los candidatos de la vacante (PLAN_EXPORTACION_CANDIDATOS_ATS):
  // no depende de la selección ni de los filtros. El backend arma el archivo
  // y registra la descarga.
  const handleExportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await api.post(
        '/applications/export',
        { positionId, language: i18n.language },
        { headers: { Authorization: `Bearer ${authToken}` }, responseType: 'blob' }
      );
      const fileName = fileNameFromDisposition(response.headers?.['content-disposition'])
        || `${t('ats.export.fileBase')} - ${positionName || positionId}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showAlert(err.response?.status === 403 ? t('ats.export.forbidden') : t('ats.export.error'), 'error');
    } finally {
      setExporting(false);
    }
  };

  const handleDragStart = (event) => {
    if (isClosed || !canEdit) return;
    const app = applications.find((a) => (a.uid || a.id) === event.active.id);
    setActiveApplication(app || null);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveApplication(null);

    if (isClosed || !canEdit) return;
    if (!over || active.id === over.id) return;

    let targetColumn = columns.find((col) => col.id === over.id);

    if (!targetColumn) {
      const overApplication = applications.find((a) => (a.uid || a.id) === over.id);
      if (overApplication) {
        targetColumn = columns.find((col) => col.id === overApplication.currentStageId);
      }
    }

    if (!targetColumn || targetColumn.isLocked) return;

    const application = applications.find((a) => (a.uid || a.id) === active.id);
    if (!application || application.currentStageId === targetColumn.id) return;

    const activeId = application.uid || application.id;
    const shouldMoveSelected = selectedApplicationIds.includes(activeId);

    const applicationsToMove = shouldMoveSelected
      ? applications.filter((a) => selectedApplicationIds.includes(a.uid || a.id) && a.status !== 'discarded')
      : [application];

    const movableApplications = applicationsToMove.filter((a) => a.currentStageId !== targetColumn.id);

    if (movableApplications.length === 0) return;

    const fromStageIds = Array.from(new Set(movableApplications.map((a) => a.currentStageId)));
    const fromColumn = fromStageIds.length === 1
      ? columns.find((col) => col.id === fromStageIds[0])
      : { name: 'Múltiples etapas' };

    setPendingMove({
      applications: movableApplications,
      targetColumn,
      fromColumn,
    });
    setMoveDialogOpen(true);
  };

  const handleConfirmMove = async (note) => {
    if (!pendingMove) return;

    const { applications: applicationsToMove, targetColumn } = pendingMove;
    const previousApplications = applications;
    const movedIds = applicationsToMove.map((application) => application.uid || application.id);

    setMoveDialogOpen(false);
    setPendingMove(null);

    onApplicationsChange(
      applications.map((a) =>
        movedIds.includes(a.uid || a.id) ? { ...a, currentStageId: targetColumn.id } : a
      )
    );

    setUpdating(true);

    try {
      const responses = await Promise.all(
        applicationsToMove.map((application) => {
          const appId = application.uid || application.id;
          const previousStageId = application.currentStageId || null;
          const fromColumn = columns.find((col) => col.id === application.currentStageId);

          return api.patch(
            `/applications/${appId}/stage`,
            {
              stageId: targetColumn.id,
              previousStageId,
              positionId,
              note: note || null,
              fromStageName: fromColumn?.name || null,
              toStageName: targetColumn.name,
              fromPhaseName: fromColumn?.phaseName || null,
              toPhaseName: targetColumn.phaseName,
            },
            { headers: { Authorization: `Bearer ${authToken}` } }
          );
        })
      );

      // Historial actualizado que devuelve el backend: la nota del movimiento
      // queda visible en la ficha (y en Estadísticas) sin recargar. Si la
      // etapa consulta antecedentes sola, trae además la revisión nueva
      // (2026-10-13); se conserva la decisión del consultor.
      const patchById = {};
      responses.forEach((res, index) => {
        const data = res?.data?.response?.data || {};
        const patch = {};
        if (Array.isArray(data.stageHistory)) patch.stageHistory = data.stageHistory;
        if (data.backgroundReview) patch.backgroundReview = data.backgroundReview;
        if (Object.keys(patch).length > 0) patchById[movedIds[index]] = patch;
      });
      if (Object.keys(patchById).length > 0) {
        onApplicationsChange((prev) => prev.map((a) => {
          const patch = patchById[a.uid || a.id];
          if (!patch) return a;
          return {
            ...a,
            ...patch,
            ...(patch.backgroundReview
              ? { backgroundReview: { ...(a.backgroundReview || {}), ...patch.backgroundReview } }
              : {}),
          };
        }));
      }

      setSelectedApplicationIds((prev) => prev.filter((id) => !movedIds.includes(id)));

      // Etapa con consulta automática y sin puntos (2026-11-01): no se
      // consultó; se avisa en vez del "movido" (quedan "Sin consultar")
      const skippedMsg = skippedAfterMoveMessage(
        t,
        responses.map((res) => res?.data?.response?.data?.backgroundCheckSkipped || null)
      );
      if (skippedMsg) {
        showAlert(skippedMsg, 'warning');
      } else {
        showAlert(
          applicationsToMove.length > 1
            ? `${applicationsToMove.length} candidatos movidos de etapa`
            : t('ats.detail.stageUpdated'),
          'success'
        );
      }
    } catch (err) {
      console.error(err);
      onApplicationsChange(previousApplications);
      showAlert(t('ats.detail.stageUpdateError'), 'error');
    } finally {
      setUpdating(false);
    }
  };

  // Por qué no se puede mover de etapa desde la ficha (null = se puede). Mismas
  // reglas que el arrastre: vacante cerrada o cancelada, sin permiso de edición,
  // o candidato fuera de las etapas (descartado, bloqueado, por validar).
  const getStageLockReason = (application) => {
    if (!application) return null;
    if (isClosed) return position?.status === 'cancelled' ? 'positionCancelled' : 'positionClosed';
    if (!canEdit) return 'noPermission';
    if (application.status === 'discarded') return 'discarded';
    if (application.status === 'blocked') return 'blocked';
    if (application.status === 'pending_validation') return 'pendingValidation';
    return null;
  };

  // Versión vigente (de la lista) del candidato abierto en la ficha: la ficha
  // guarda la foto del momento en que se abrió y lee de aquí la etapa y el
  // historial actualizados después de moverlo.
  const selectedApplicationId = selectedApplication
    ? (selectedApplication.uid || selectedApplication.id)
    : null;
  const liveSelectedApplication = selectedApplicationId
    ? applications.find((a) => (a.uid || a.id) === selectedApplicationId) || null
    : null;

  useEffect(() => {
    openApplicationIdRef.current = drawerOpen ? selectedApplicationId : null;
  }, [drawerOpen, selectedApplicationId]);

  // Navegación de la ficha: posición del abierto entre los que siguen en la
  // etapa (-1 si ya salió de ella), total y anterior / siguiente
  const drawerNavigation = (() => {
    if (!drawerNav || !selectedApplicationId) return null;
    const remainingIds = drawerNav.ids.filter(isInDrawerNavStage);
    const prevId = getDrawerNeighborId(selectedApplicationId, -1);
    const nextId = getDrawerNeighborId(selectedApplicationId, 1);
    return {
      index: remainingIds.indexOf(selectedApplicationId),
      total: remainingIds.length,
      stageName: columns.find((col) => col.id === drawerNav.stageId)?.name || '',
      onPrev: prevId ? () => goToApplication(prevId) : null,
      onNext: nextId ? () => goToApplication(nextId) : null,
    };
  })();

  // Mover de etapa desde la ficha: mismo diálogo y misma confirmación que el
  // arrastre (handleConfirmMove), solo con este candidato (nunca la selección
  // múltiple). Se toma la versión vigente para que la etapa de origen sea la
  // actual aunque ya se haya movido con la ficha abierta.
  const handleMoveFromDrawer = (application, stageId) => {
    if (!application || updating) return;
    const id = application.uid || application.id;
    const current = applications.find((a) => (a.uid || a.id) === id);
    if (!current || getStageLockReason(current)) return;
    if (current.currentStageId === stageId) return;
    const targetColumn = columns.find((col) => col.id === stageId);
    if (!targetColumn || targetColumn.isLocked) return;
    setPendingMove({
      applications: [current],
      targetColumn,
      fromColumn: columns.find((col) => col.id === current.currentStageId) || null,
    });
    setMoveDialogOpen(true);
  };

  if (columns.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('ats.pipeline.noPipeline')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('ats.detail.currentPhase')}:
        </Typography>
        <Chip
          label={currentPhase?.name || '—'}
          size="small"
          sx={{ backgroundColor: '#6A5AF9', color: '#fff', fontWeight: 600 }}
        />
        {updating && <CircularProgress size={14} sx={{ ml: 1 }} />}
        {isClosed && (
          <Chip
            label={t('ats.detail.processClosed')}
            size="small"
            color="error"
            variant="outlined"
          />
        )}

        {reviewCount > 0 && (
          <Tooltip title={t('ats.backgroundReview.filterHint')} arrow>
            <Chip
              label={`${t('ats.backgroundReview.filterChip')} (${reviewCount})`}
              size="small"
              onClick={() => setReviewFilter((prev) => !prev)}
              sx={{
                ml: 1,
                fontWeight: 700,
                cursor: 'pointer',
                backgroundColor: reviewFilter ? '#C2410C' : '#FFEDD5',
                color: reviewFilter ? '#fff' : '#C2410C',
              }}
            />
          </Tooltip>
        )}

        {selectedApplicationIds.length > 0 && (
          <>
            <Chip
              label={`${selectedApplicationIds.length} seleccionados`}
              size="small"
              variant="outlined"
              sx={{ ml: 1 }}
            />
            {canEdit && (
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={() => setBulkDiscardOpen(true)}
                sx={{ fontSize: '0.75rem' }}
              >
                {t('ats.detail.discard')} ({selectedApplicationIds.length})
              </Button>
            )}
            {canEdit && (
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<Send sx={{ fontSize: 15 }} />}
                onClick={() => setSendToClientOpen(true)}
                sx={{ fontSize: '0.75rem' }}
              >
                {t('ats.sendClient.button')} ({selectedApplicationIds.length})
              </Button>
            )}
            {/* Solicitud masiva de documentos a los seleccionados (PLAN_SOLICITUD_DOCUMENTOS_MASIVA) */}
            {canEdit && (
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={<UploadFile sx={{ fontSize: 15 }} />}
                onClick={() => setBulkDocumentsOpen(true)}
                sx={{ fontSize: '0.75rem' }}
              >
                {t('ats.documents.bulk.toolbarButton')} ({selectedApplicationIds.length})
              </Button>
            )}
            <Button
              size="small"
              onClick={handleClearSelection}
              sx={{ fontSize: '0.75rem' }}
            >
              Limpiar selección
            </Button>
          </>
        )}

        {/* Excel con todos los candidatos de la vacante (PLAN_EXPORTACION_CANDIDATOS_ATS) */}
        {canEdit && (
          <Tooltip
            title={applications.length === 0 ? t('ats.export.empty') : t('ats.export.tooltip')}
            arrow
          >
            <Box component="span" sx={{ ml: 'auto' }}>
              <Button
                size="small"
                color="primary"
                variant="outlined"
                startIcon={exporting
                  ? <CircularProgress size={14} />
                  : <FileDownload sx={{ fontSize: 16 }} />}
                onClick={handleExportExcel}
                disabled={exporting || applications.length === 0}
                sx={{ fontSize: '0.75rem' }}
              >
                {exporting ? t('ats.export.downloading') : t('ats.export.button')}
              </Button>
            </Box>
          </Tooltip>
        )}
      </Box>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, alignItems: 'flex-start' }}>
          {columns.map((column) => (
            <AtsStageColumn
              key={column.id}
              stage={column}
              applications={getApplicationsByStage(column.id)}
              isLocked={column.isLocked}
              canEdit={canEdit}
              phaseName={column.phaseName}
              onCardClick={handleCardClick}
              position={position}
              positionId={positionId}
              selectedApplicationIds={selectedApplicationIds}
              onSelectionChange={handleSelectionChange}
              onBulkSelectionChange={handleBulkSelectionChange}
              onStageAction={handleStageAction}
              cvScores={cvScores}
              onCvScoresRefresh={onCvScoresRefresh}
              allApplications={applications}
              onPositionChange={onPositionChange}
              onApplicationPatch={handleApplicationPatch}
            />
          ))}

          {/* Columna "Por validar" (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): no es
              una etapa del pipeline. Cada tarjeta muestra el motivo, el teléfono
              para contactar a la persona y "Validar" (quien puede editar). */}
          <Box data-testid="pending-validation-column" sx={{ display: 'flex', flexDirection: 'column', minWidth: 200, maxWidth: 220 }}>
            <Box sx={{
              px: 1.5, py: 1,
              backgroundColor: '#FFFBEB',
              borderRadius: '8px 8px 0 0',
              borderBottom: '2px solid #D97706',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#92400E' }}>
                  {t('ats.pipeline.pendingValidationColumn')}
                </Typography>
                <Chip
                  label={pendingValidationApplications.length}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#D97706', color: '#fff' }}
                />
              </Box>
            </Box>
            <Box sx={{
              flex: 1, minHeight: 300, p: 1,
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB', borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              display: 'flex', flexDirection: 'column', gap: 1,
            }}>
              {pendingValidationApplications.length === 0 ? (
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px dashed #D1D5DB', borderRadius: 1, minHeight: 60,
                }}>
                  <Typography variant="caption" color="text.disabled">
                    {t('ats.pipeline.noPendingValidation')}
                  </Typography>
                </Box>
              ) : (
                pendingValidationApplications.map((application) => (
                  <CandidateCard
                    key={application.uid || application.id}
                    application={application}
                    onClick={() => handleCardClick(application)}
                    cvScore={getCvScore(application)}
                    onValidate={canEdit && !isClosed ? (app) => setValidateTarget(app) : null}
                  />
                ))
              )}
            </Box>
          </Box>

          {/* Columna de descartados — solo lectura (no es una etapa real del pipeline) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200, maxWidth: 220 }}>
            <Box sx={{
              px: 1.5, py: 1,
              backgroundColor: '#FEF2F2',
              borderRadius: '8px 8px 0 0',
              borderBottom: '2px solid #EF4444',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#991B1B' }}>
                  {t('ats.pipeline.discardedColumn')}
                </Typography>
                <Chip
                  label={discardedApplications.length}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#EF4444', color: '#fff' }}
                />
              </Box>
            </Box>
            <Box sx={{
              flex: 1, minHeight: 300, p: 1,
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB', borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              display: 'flex', flexDirection: 'column', gap: 1,
            }}>
              {discardedApplications.length === 0 ? (
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px dashed #D1D5DB', borderRadius: 1, minHeight: 60,
                }}>
                  <Typography variant="caption" color="text.disabled">
                    {t('ats.pipeline.noDiscarded')}
                  </Typography>
                </Box>
              ) : (
                discardedApplications.map((application) => (
                  <CandidateCard
                    key={application.uid || application.id}
                    application={application}
                    onClick={() => handleCardClick(application)}
                    cvScore={getCvScore(application)}
                  />
                ))
              )}
            </Box>
          </Box>

          {/* Columna de bloqueados — solo lectura */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 200, maxWidth: 220 }}>
            <Box sx={{
              px: 1.5, py: 1,
              backgroundColor: '#F3F4F6',
              borderRadius: '8px 8px 0 0',
              borderBottom: '2px solid #374151',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#374151' }}>
                  {t('ats.pipeline.blockedColumn')}
                </Typography>
                <Chip
                  label={blockedApplications.length}
                  size="small"
                  sx={{ height: 18, fontSize: '0.65rem', backgroundColor: '#374151', color: '#fff' }}
                />
              </Box>
            </Box>
            <Box sx={{
              flex: 1, minHeight: 300, p: 1,
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB', borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              display: 'flex', flexDirection: 'column', gap: 1,
            }}>
              {blockedApplications.length === 0 ? (
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px dashed #D1D5DB', borderRadius: 1, minHeight: 60,
                }}>
                  <Typography variant="caption" color="text.disabled">
                    {t('ats.pipeline.noBlocked')}
                  </Typography>
                </Box>
              ) : (
                blockedApplications.map((application) => (
                  <CandidateCard
                    key={application.uid || application.id}
                    application={application}
                    onClick={() => handleCardClick(application)}
                    cvScore={getCvScore(application)}
                  />
                ))
              )}
            </Box>
          </Box>
        </Box>

        <DragOverlay>
          {activeApplication && (
            <CandidateCard
              application={activeApplication}
              isDragging
              isSelected={selectedApplicationIds.includes(activeApplication.uid || activeApplication.id)}
              cvScore={getCvScore(activeApplication)}
            />
          )}
        </DragOverlay>
      </DndContext>

      {/* key por postulación: al pasar de un candidato a otro la ficha se
          vuelve a montar y carga todo de nuevo, como al abrirla desde el
          tablero (sin datos ni respuestas tardías del anterior) */}
      <AtsApplicationDrawer
        key={selectedApplicationId || 'none'}
        open={drawerOpen}
        onClose={closeDrawer}
        application={selectedApplication}
        position={position}
        positionId={positionId}
        canEdit={canEdit}
        cvScore={getCvScore(selectedApplication)}
        onDiscard={handleDiscardFromDrawer}
        onRestore={handleRestore}
        rejectionTemplate={rejectionTemplate}
        autoSendDefault={autoSendDefault}
        positionName={positionName}
        orgName={orgName}
        canBlock={isAdminPlus}
        canUnblock={isSuperAdmin}
        onBlockClick={(app) => setBlockTarget(app)}
        onUnblock={handleUnblock}
        onPositionChange={onPositionChange}
        onApplicationChange={handleApplicationPatch}
        liveApplication={liveSelectedApplication}
        onMoveStage={handleMoveFromDrawer}
        stageLockReason={getStageLockReason(liveSelectedApplication || selectedApplication)}
        movingStage={updating}
        navigation={drawerNavigation}
        instantOpen={drawerInstant}
        onNotesChange={(appId, patch) => {
          // Solo sincroniza el estado del kanban; el drawer mantiene su propio
          // estado local de notas (evita re-fetch de tests al editar notas).
          // Funcional: una respuesta que llega después de pasar a otro
          // candidato no revierte otros cambios de la lista.
          handleApplicationPatch(appId, patch);
        }}
      />

      <AtsBulkDiscardDialog
        open={bulkDiscardOpen}
        onClose={() => setBulkDiscardOpen(false)}
        count={selectedApplicationIds.length}
        rejectionTemplate={rejectionTemplate}
        positionName={positionName}
        orgName={orgName}
        autoSendDefault={autoSendDefault}
        onConfirm={handleBulkDiscard}
      />

      <AtsSendToClientDialog
        open={sendToClientOpen}
        onClose={() => setSendToClientOpen(false)}
        applications={applications.filter((a) => selectedApplicationIds.includes(a.uid || a.id))}
        positionId={positionId}
        positionName={positionName}
        orgName={orgName}
        onConfirm={handleSendToClient}
      />

      {/* Mismo modal que el botón del módulo, directo en "Nueva solicitud" con
          los seleccionados; al cerrarlo después de enviar se limpia la selección */}
      <AtsBulkRequestDocumentsDialog
        open={bulkDocumentsOpen}
        onClose={() => setBulkDocumentsOpen(false)}
        mode="selection"
        applications={applications.filter((a) => selectedApplicationIds.includes(a.uid || a.id))}
        positionId={positionId}
        onApplicationPatch={handleApplicationPatch}
        onFinished={handleClearSelection}
      />

      <AtsBlockDialog
        open={!!blockTarget}
        onClose={() => setBlockTarget(null)}
        candidateName={blockTarget?.candidateName || blockTarget?.candidateEmail}
        onConfirm={(reason) => handleBlock(blockTarget, reason)}
      />

      <AtsOfferDialog
        open={!!offerTarget}
        onClose={() => setOfferTarget(null)}
        application={offerTarget}
        positionName={positionName}
        orgName={orgName}
        onOfferChange={handleOfferChange}
      />

      <AtsHireDialog
        open={!!hireTarget}
        onClose={() => setHireTarget(null)}
        application={hireTarget}
        positionName={positionName}
        orgName={orgName}
        onHireChange={handleHireChange}
      />

      <AtsValidateDialog
        open={!!validateTarget}
        application={validateTarget}
        onClose={() => setValidateTarget(null)}
        onValidated={handleValidated}
      />

      {pendingMove && (
        <AtsMoveStageDialog
          open={moveDialogOpen}
          onConfirm={handleConfirmMove}
          onCancel={() => { setMoveDialogOpen(false); setPendingMove(null); }}
          fromStage={pendingMove.fromColumn}
          toStage={pendingMove.targetColumn}
          candidateName={
            pendingMove.applications.length > 1
              ? `${pendingMove.applications.length} candidatos seleccionados`
              : pendingMove.applications[0]?.candidateName ||
              pendingMove.applications[0]?.candidateEmail
          }
        />
      )}
    </Box>
  );
};

export default AtsKanbanPipeline;